/**
 * Sprint T-17.1 · Notification Center · DlqTab.
 * Listado + retry + discard · patrón idéntico a T-16.1.
 * §4.3
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ShieldCheck, RefreshCw, Trash2 } from "lucide-react";
import { WidgetCard, EmptyState, LoadingState, CitationBadge, MetricCard, WidgetGrid } from "../advisor";
import { useNotificationDlq, useNotifDlqRetry, useNotifDlqDiscard } from "../../hooks/useNotifications";
import { RelativeTime } from "../automationCenter/shared/Formatters";
import ChannelIcon, { ALL_CHANNELS } from "./shared/ChannelIcon";
import { DlqRetryDialog, DlqDiscardDialog } from "./shared/Dialogs";

const PAGE_SIZE = 25;

function DlqRow({ entry, onRetry, onDiscard, index }) {
  const reviewed = Boolean(entry.reviewed_at);
  return (
    <div
      data-testid={`notif-dlq-row-${index}`}
      className={`rounded border p-3 ${
        reviewed
          ? "border-slate-500/30 bg-slate-500/5 opacity-60"
          : "border-red-500/30 bg-red-500/5"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <ChannelIcon channel={entry.channel} size={14} />
          <div className="min-w-0">
            <div className="font-mono text-xs font-semibold truncate">
              {entry.template_id}
            </div>
            <div className="mt-0.5 text-[10px] text-[var(--ag-text-dim)] truncate">
              → {entry.to || "—"}
            </div>
            <div className="mt-0.5 text-[10px] text-[var(--ag-text-dim)]">
              attempts <span className="font-mono">{entry.attempts}</span> · moved{" "}
              <RelativeTime iso={entry.moved_at} />
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {reviewed ? (
            <span className="rounded border border-slate-500/30 bg-slate-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-slate-400">
              {entry.action || "reviewed"}
            </span>
          ) : (
            <>
              <button
                type="button"
                data-testid={`notif-dlq-retry-btn-${index}`}
                onClick={() => onRetry(entry)}
                className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-500 hover:bg-emerald-500/20"
              >
                <RefreshCw className="h-3 w-3" /> Retry
              </button>
              <button
                type="button"
                data-testid={`notif-dlq-discard-btn-${index}`}
                onClick={() => onDiscard(entry)}
                className="inline-flex items-center gap-1 rounded border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] text-red-500 hover:bg-red-500/20"
              >
                <Trash2 className="h-3 w-3" /> Descartar
              </button>
            </>
          )}
        </div>
      </div>
      {entry.last_error && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[10px] text-[var(--ag-text-dim)]">Ver error</summary>
          <div className="mt-1 rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)] p-2 text-[10px]">
            <code className="font-mono text-red-500">{entry.last_error?.type || "—"}</code>
            <span className="ml-2 whitespace-pre-wrap">
              {(entry.last_error?.message || "").slice(0, 400)}
            </span>
          </div>
        </details>
      )}
    </div>
  );
}

export default function DlqTab() {
  const [page, setPage] = useState(0);
  const [channel, setChannel] = useState(undefined);
  const [retryFor, setRetryFor] = useState(null);
  const [discardFor, setDiscardFor] = useState(null);

  const q = useNotificationDlq({ channel, limit: PAGE_SIZE, skip: page * PAGE_SIZE });
  const retry = useNotifDlqRetry();
  const discard = useNotifDlqDiscard();

  const items = q.data?.items || [];
  const total = q.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pending = items.filter((i) => !i.reviewed_at).length;

  const doRetry = async () => {
    try {
      await retry.mutateAsync(retryFor.id);
      toast.success("Reintento encolado · el sweep lo procesará");
      setRetryFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reintentar");
    }
  };

  const doDiscard = async () => {
    try {
      await discard.mutateAsync(discardFor.id);
      toast.success("Entrada descartada");
      setDiscardFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al descartar");
    }
  };

  return (
    <section className="space-y-4" data-testid="notif-dlq-tab">
      <WidgetGrid cols="md:grid-cols-3">
        <MetricCard label="Total DLQ" value={total} testId="notif-dlq-total" hint="acumulado" />
        <MetricCard label="Sin revisar" value={pending} testId="notif-dlq-pending" hint="requiere acción" />
        <MetricCard label="Revisadas" value={items.length - pending} testId="notif-dlq-reviewed" hint="página actual" />
      </WidgetGrid>

      <WidgetCard
        title={`Dead Letter Queue · ${total} entries`}
        description="Notificaciones que agotaron reintentos o fallaron con error permanente."
        citations={["/api/notifications/dlq"]}
        testId="notif-dlq-list"
        actions={
          <select
            data-testid="notif-dlq-filter-channel"
            value={channel || ""}
            onChange={(e) => { setChannel(e.target.value || undefined); setPage(0); }}
            className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-0.5 text-[10px]"
          >
            <option value="">Todos los canales</option>
            {ALL_CHANNELS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        }
      >
        {q.isLoading && <LoadingState />}
        {q.error && <EmptyState message="No se pudo cargar la DLQ." testId="notif-dlq-error" />}
        {q.data && total === 0 && (
          <div
            data-testid="notif-dlq-empty"
            className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-500"
          >
            <ShieldCheck className="mr-1 inline h-3 w-3" />
            Sin fallos en DLQ · el dispatcher está sano.
          </div>
        )}
        {items.length > 0 && (
          <div className="space-y-2" data-testid="notif-dlq-list-inner">
            {items.map((e, i) => (
              <DlqRow
                key={e.id}
                entry={e}
                index={i}
                onRetry={setRetryFor}
                onDiscard={setDiscardFor}
              />
            ))}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div data-testid="notif-dlq-pagination" className="mt-3 flex items-center justify-between text-xs">
            <span className="text-[var(--ag-text-dim)]">
              Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="notif-dlq-prev"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] disabled:opacity-30"
              >
                <ChevronLeft className="h-3 w-3" /> Anterior
              </button>
              <span className="px-2 text-[10px] tabular-nums">pág {page + 1} / {totalPages}</span>
              <button
                type="button"
                data-testid="notif-dlq-next"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= total}
                className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] disabled:opacity-30"
              >
                Siguiente <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </WidgetCard>

      {retryFor && (
        <DlqRetryDialog
          entry={retryFor}
          onClose={() => setRetryFor(null)}
          onConfirm={doRetry}
          pending={retry.isPending}
        />
      )}
      {discardFor && (
        <DlqDiscardDialog
          entry={discardFor}
          onClose={() => setDiscardFor(null)}
          onConfirm={doDiscard}
          pending={discard.isPending}
        />
      )}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["retry/discard auditados T-14", "audit.notifications.dlq.*"]} />
      </footer>
    </section>
  );
}
